import { Component, OnDestroy, OnInit } from '@angular/core';
import { cloneDeep } from "lodash";
import ExpandMenu from '../../../../assets/data/expanded-menu.json';
import { ApiPropertiesFactory } from '@shared/factories/api-properties/api-properties.factory';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ContextFactory } from '@shared/factories/context/context.factory';
import { GlobalCfgFactory } from '@shared/factories/global-cfg/global-cfg.factory';
import { LoaderFactory } from '@shared/factories/loader/loader.factory';
import { AfTranslateFactory } from '@shared/modules/translate/factory/translate.factory';
import { ApiServiceService } from '@shared/services/api-service/api-service.service';
import { CommonUtilsService } from '@shared/services/common-utils/common-utils.service';
import { ModalServiceService } from '@shared/services/modal-service/modal-service.service';
import { BroadcastService } from '@shared/services/broadcast/broadcast.service';
import { DocumentActionsService } from '@shared/services/document-actions/document-actions.service';
import { ExtensionDocumentPipe } from '../../../shared/filters/extension-document/extension-document.pipe';
import { ConvertDatePipe } from '../../../shared/filters/convert-date/convert-date.pipe';
import { AfTranslatePipe } from '@shared/modules/translate/pipe/af-translate.pipe';
import { AfOrderByPipe } from '@shared/filters/af-order-by/af-order-by.pipe';
import { TooltipModule } from 'primeng/tooltip';
import { NgClass } from '@angular/common';
import { AfTranslateDirective } from '../../../shared/modules/translate/directive/af-translate.directive';
import { ActivatedRoute } from '@angular/router';
import { FieldItemComponent } from "../../../shared/components/field-item/field-item.component";
import { ButtonWithTextMenuArrowComponent } from "../../../shared/components/button-with-text-menu-arrow/button-with-text-menu-arrow.component";
import { SpanTooltipComponent } from "../../../shared/components/span-tooltip/span-tooltip.component";
import { ButtonWithMenuActionComponent } from "../../../shared/components/button-with-menu-action/button-with-menu-action.component";
import { Subject } from 'rxjs';

@Component({
  selector: 'reference-document',
  templateUrl: './reference-document.component.html',
  standalone: true,
  imports: [
    AfTranslateDirective,
    NgClass,
    TooltipModule,
    AfOrderByPipe,
    AfTranslatePipe,
    ConvertDatePipe,
    ExtensionDocumentPipe,
    FieldItemComponent,
    ButtonWithTextMenuArrowComponent,
    SpanTooltipComponent,
    ButtonWithMenuActionComponent
  ]
})
export class ReferenceDocumentComponent implements OnInit, OnDestroy {
  constructor(private apiProperties: ApiPropertiesFactory,
    private globalCfg: GlobalCfgFactory,
    private context: ContextFactory,
    private modalService: ModalServiceService,
    private translate: AfTranslateFactory,
    private loader: LoaderFactory,
    private apiService: ApiServiceService,
    private commonUtilsService: CommonUtilsService,
    private broadcastService: BroadcastService,
    private documentActions: DocumentActionsService,
    private dialogRef: DynamicDialogRef<ReferenceDocumentComponent>,
    private dialogConfig: DynamicDialogConfig,
    private route: ActivatedRoute) { }

  skin: string = this.globalCfg.skin;
  configuration:any = this.dialogConfig.data;
  hasTranslations: boolean = false;
  hasTranslationsSubject: Subject<boolean> = new Subject<boolean>();
  expandedMenu: any = ExpandMenu;
  referenceDocument:any = {
    // defaultLanguage: (this.skin === 'uk-steel-blue' || this.skin === 'steel-blue') ? this.context.settings.defaultProduct +'_' + this.apiProperties.defaultLanguage : this.apiProperties.defaultLanguage,
    dataProtectionView: (this.skin === 'dark-blue' || this.skin === 'steel-blue' || this.skin === 'gm-law'),
    isEquality: this.context.settings.defaultProduct === 'igualdad' || this.context.settings.defaultProduct === 'igualdad02',
    languagesApp: cloneDeep(this.context.settings.languagesApp),
    evaluationId: this.route.snapshot.firstChild?.params['evaluationId'],
    actionMenu: { expandedMenu: this.expandedMenu['menuEditDelete'].menu},
    actionMenuForm: {},
    documentationList: [],
    legalData: [],
    additionalData: [],
    protectionData: []
  };

  ngOnInit(): void {
    this.configureCalls();
  }

  configureCalls(){
    if(this.configuration.additionalCfg?.hasReferenceDocuments){
      this.getDocumentationList();
    }
    if(this.referenceDocument.dataProtectionView){
      if(this.configuration.additionalCfg?.hasLegalInfo){
        this.getLegalData();
      }else{
        this.configureReferenceDocument('legal');
      }
      if(this.configuration.additionalCfg?.hasDataTreatments){
        this.getProtectionData();
      }else{
        this.configureReferenceDocument('protection');
      }
      if(this.configuration.additionalCfg?.hasExtraInfo){
        this.getAdditionalData();
      }else{
        this.configureReferenceDocument('additional');
      }
    }
    if(this.skin !== 'steel-blue' && !this.configuration.concept.isFront && this.configuration.concept.communicationChannel){
      this.getConceptInfo();
      this.getFieldContainers();
    }
  }

  getDocumentationList(){
    this.apiService.get('publicconceptdoc/concept', this.configuration.concept.conceptId)
      .then((data:any)=>{
        this.referenceDocument.documentationList = data;
        this.getFormatDocument(this.referenceDocument.documentationList);
      }, (errorData:any)=>{})
  }

  getFormatDocument(documentationList: any){
    for(let i = 0; i < documentationList.length; i++){
      let document = documentationList[i];
      let name = document.name;
      document.extension = name.substr(name.lastIndexOf('.') + 1, name.length);
    }
  }

  getLegalData(){
    this.apiService.get('publicconceptcfg/concept', this.configuration.concept.conceptId + '/legal-info')
      .then((data:any)=>{
        this.referenceDocument.legalData = data;
        this.configureReferenceDocument('legal');
      }, (errorData:any)=>{})
  }

  getProtectionData(){
    this.apiService.get('publicconceptcfg/concept', this.configuration.concept.conceptId + '/data-treatments')
    .then((data:any)=>{
      this.referenceDocument.protectionData = data;
      this.configureReferenceDocument('protection');
    }, (errorData:any)=>{})
  }

  getAdditionalData(){
    this.apiService.get('publicconceptcfg/concept', this.configuration.concept.conceptId + '/extra-info')
      .then((data:any)=>{
        this.referenceDocument.additionalData = data;
        this.configureReferenceDocument('additional');
      }, (errorData:any)=>{})
  }

  getConceptInfo(){
    this.apiService.get('concepts', this.referenceDocument.evaluationId + '/' + this.configuration.concept.conceptId)
      .then((data:any)=>{
        // data.disabled = data.publicShared || data.shared;
        this.referenceDocument.conceptInfo = data;
      })
  }

  getFieldContainers(){
    let url : string = 'fieldcontainers/'+this.referenceDocument.evaluationId+'/'+this.configuration.concept.conceptId;
    this.apiService.get(url)
      .then((data:any) => {
        if(data?.length){
          if(data[0].fields?.length){
            this.referenceDocument.fieldContainerConfigurated = data[0];
            this.referenceDocument.fieldContainerConfiguratedPreview = cloneDeep(data[0]);
            this.referenceDocument.fieldContainerConfiguratedTranslation = cloneDeep(data[0]);
            this.getFieldContainerTranslations();
          }else{
            this.deleteAdditionalGroup(data[0]);
          }
        }
      }, (errorData:any)=>{
        this._updateHasTranslationsSubject();
      });
  }

  getFieldContainerTranslations() {
    let defaultLanguage = this.apiProperties.defaultLanguage;
    let availableLanguages = [...this.apiProperties.availableLanguages];
    availableLanguages.splice(availableLanguages.indexOf(defaultLanguage), 1);
    this.apiService.get(`fieldcontainers/translations/fieldcontainer/${this.referenceDocument.fieldContainerConfiguratedPreview.fieldContainerId}/${availableLanguages.join('|')}`)
      .then((data: any) => {
        this.referenceDocument.fieldContainerTranslations = data;
        this.checkTranslatedLanguages();
        this.addTranslationsToSelect();
        this._updateHasTranslationsSubject();
      },(errorData: any) => {
        this._updateHasTranslationsSubject();
      });
  }

  private _updateHasTranslationsSubject() {
    this.hasTranslations = true;
    this.hasTranslationsSubject.next(this.hasTranslations);
  }

  checkTranslatedLanguages() {
    for (let translation of this.referenceDocument.fieldContainerTranslations) {
      translation.isTranslated = this.checkTranslation(translation);
    }
  }

  checkTranslation(translation: any): boolean {
    if (translation.translatedName) {
      return true;
    }

    for (let field of translation.fields) {
      if (field.translatedLabel
        || field.translatedExtendedLabel
        || field.translatedDescription
        || field.translatedDefaultValue
        || field.translatedVoiceRecordOkText) {
        return true;
      }
      for (let listValue of field.listValues) {
        if (listValue.translatedText) {
          return true;
        }
      }
    }
    return false;
  }

  addTranslationsToSelect() {
    let translatedLanguages = [this.apiProperties.defaultLanguage];
    for (let translation of this.referenceDocument.fieldContainerTranslations) {
      if (translation.isTranslated) {
        translatedLanguages.push(translation.language);
      }
    }
    this.referenceDocument.formTranslatedLanguages = this.commonUtilsService.configureAvailableLanguages(translatedLanguages);
    this.referenceDocument.formTranslatedLanguageSelected = this.referenceDocument.formTranslatedLanguages[0];
  }

  changeFormTranslation(language: any) {
    this.referenceDocument.formTranslatedLanguageSelected = language;
    if (this.referenceDocument.formTranslatedLanguageSelected.listValueId === this.apiProperties.defaultLanguage) {
      this.referenceDocument.fieldContainerConfiguratedTranslation = cloneDeep(this.referenceDocument.fieldContainerConfigurated);
    } else {
      this.changeFormToTranslation();
    }
  }

  changeFormToTranslation(): any {
    let that = this.referenceDocument;
    let container = cloneDeep(that.fieldContainerConfiguratedTranslation);
    let currentTranslation: any = {};
    for (let translation of that.fieldContainerTranslations) {
      if (translation.language === that.formTranslatedLanguageSelected.listValueId) {
        currentTranslation = translation;
        break;
      }
    }
    if (!currentTranslation) {
      return;
    }

    container.name = currentTranslation.translatedName ?? currentTranslation.originalName;
    for (let i = 0; i < container.fields.length; i++) {
      let containerField = container.fields[i];
      let translationField = currentTranslation.fields[i];
      containerField.label = translationField.translatedLabel ?? translationField.originalLabel;
      containerField.extendedLabel = translationField.translatedExtendedLabel ?? translationField.originalExtendedLabel;
      containerField.description = translationField.translatedDescription ?? translationField.originalDescription;
      containerField.defaultValue = translationField.translatedDefaultValue ?? translationField.originalDefaultValue;
      for (let x = 0; x < containerField.listValues.length; x++) {
        containerField.listValues[x].text = translationField.listValues[x].translatedText ?? translationField.listValues[x].originalText;
      }
    }
    that.fieldContainerConfiguratedTranslation = container;
  }

  configureReferenceDocument(type:string){
    this.referenceDocument[type + 'SelectedLanguages'] = [];
    if(this.referenceDocument[type + 'Data'] && this.referenceDocument[type + 'Data'].length){
      this.configureSelectedLanguages(type);
    }else if(type === 'additional'){
      this.selectListLanguagesSelected(type, this.apiProperties.defaultLanguage, true, '');
    }else{
      this.getDefaultLanguage(type);
    }
  }

  getDefaultLanguage(type: any){
    let textToTranslate: string = type === 'protection'? 'text-default' : 'text-default-legal';
    if(this.translate.hasLanguage(this.apiProperties.defaultLanguage)){
      let textDefault = this.translate.instantSiteTerms(textToTranslate, this.apiProperties.defaultLanguage);
      this.selectListLanguagesSelected(type, this.apiProperties.defaultLanguage, true, textDefault);
    }else{
      this.translate.getTranslates(this.apiProperties.defaultLanguage).then((data:any)=>{
        let textDefault = this.translate.instantSiteTerms(textToTranslate, this.apiProperties.defaultLanguage);
        this.selectListLanguagesSelected(type, this.apiProperties.defaultLanguage, true, textDefault);
      }, (errorData:any)=>{})
    }
  }

  configureSelectedLanguages(type:string){
    let configData = this.referenceDocument[type + 'Data'];
    for(let i = 0; i < configData.length; i++){
      let selectedLanguage = configData[i];
      let textDefault = selectedLanguage.language === this.apiProperties.defaultLanguage;
      this.selectListLanguagesSelected(type, selectedLanguage.language, textDefault, selectedLanguage.text);
    }
  }

  selectListLanguagesSelected(type:string, selectedLanguage:string, textDefault:boolean, textDescription:string){
    for(let i = 0; i < this.referenceDocument.languagesApp.length; i++){
      let language = cloneDeep(this.referenceDocument.languagesApp[i]);
      language.indexListLanguage = i;
      if(language.listValueId === selectedLanguage){
        language.selected = true;
        language.textDescription = textDescription;
        language.defaultOption = textDefault ? true : false;
        language.active = language.defaultOption ? true : false;
        this.referenceDocument[type + 'SelectedLanguages'].push(language);
        this.referenceDocument.chargedLiteralDefault = true;
        if(language.active){
          this.referenceDocument[type + 'LanguageSelected'] = language;
        }
        break;
      }
    }
  }

  addDocument(){
    this.modalService.uploadDocuments('addNewDocumentsConcept', [], this.referenceDocument.documentationList, this.configuration.concept).subscribe((result:any) => {
      if(result.result === 'ok'){
        this.upDocument(result);
         this.modalService.successModal({ text: this.translate.instant('documentation-list.documentation-added') });
      }
    });
  }

  upDocument(result:any){
    let files = result.files;
    for(let i = 0; i < files.length; i++){
      let document = files[i];
      let name = document.name;
      document.extension = name.substr(name.lastIndexOf('.')+1, name.length);
      document.creator = this.context.user.fullName;
      this.referenceDocument.documentationList.push(document);
    }
  }

  launchActionDocument(document:any, action:string){
    switch(action){
      case 'delete':
        this.openQuestionModal(document);
        break;
      case 'download':
        this.documentActions.doAction({ action: 'download', file: document} );
        break;
      default:
        break;
    }
  }

  openQuestionModal(document:any){
    this.modalService.infoModal({ text: this.translate.instant('documentation-list.question-delete-document'), typeButtons: 'cancelRemove' })
      .subscribe((result:any) => {
        if(result.result === 'ok'){
          this.deleteDocumentSelected(document);
        }
      });
  }

  deleteDocumentSelected(document:any){
    this.loader.openLoader('documentation-list-delete-document');
    this.apiService.delete('publicconceptdoc/', document.sharedPublicConceptDocumentId).then(
      (data:any) => {
        this.referenceDocument.documentationList = this.commonUtilsService.deleteElement(document, this.referenceDocument.documentationList, 'sharedPublicConceptDocumentId');
        this.modalService.successModal({ text: this.translate.instant('common.the-document') + "'" + document.name + "'" +  this.translate.instant('documentation-list.remove-document') });
      },
      (error:any) => {
        console.error(error);
        this.modalService.errorModal({ text: this.translate.instant('documentation-list.error-remove-document') + "'" + document.name + "'" });
      }
    ).finally(() => {
      this.loader.closeLoader('documentation-list-delete-document');
    });
  }

  configureData(type:string){
    let that = this.referenceDocument;
    let configurationModal = {
        // defaultLanguage: sessionStorage.getItem('selectLanguage'),
        defaultLanguage: this.apiProperties.defaultLanguage,
        concept: this.configuration.concept,
        // protectionData: that[type + 'Data'],
        languagesApp: that.languagesApp,
        selectedLanguages: that[type + 'SelectedLanguages'],
        languageSelected: that[type + 'LanguageSelected'],
        aditionalInformation: type
    };

    this.modalService.protectionDataModal(configurationModal).subscribe((result:any) => {
      if(result.result === 'ok'){
        let action = this.referenceDocument[type + 'Data'] && this.referenceDocument[type + 'Data'].length ? 'edit' : 'add';
        this.changeData(type, result.selectedLanguages, action);
      }
    });
  }

  changeData(type:string, selectedLanguages:any, action:string){
    let that = this.referenceDocument;
    this.loader.openLoader('change-data-protection');
    let url: string = 'publicconceptcfg/concept/' + this.configuration.concept.conceptId;
    let extraUrl: string = this.getUpdateUrl(type);
    this.apiService.update( url, extraUrl, selectedLanguages).then(
      (data:any) => {
        that[type + 'SelectedLanguages'] = [];
        this.referenceDocument[type + 'Data'] = selectedLanguages;
        that.languagesApp.forEach((language:any) => {
          language.selected = false;
        });
        let text =  this.translate.instant('reference-document-modal.save-data-protection-ok');
        if(action === 'delete'){
          let textDefault = this.getTextDefault(type);
          this.selectListLanguagesSelected(type, this.apiProperties.defaultLanguage, true, textDefault);
          text =  this.translate.instant('reference-document-modal.delete-data-protection-ok');
        }else{
          this.configureSelectedLanguages(type);
        }
        this.broadcastService.broadcast('changeData' + type, {[type + 'Data']: this.referenceDocument[type + 'Data']});
        this.modalService.successModal({ text: text });
      },
      (error:any) => {
        console.error(error);
        this.modalService.errorModal({ text: this.translate.instant('field-type-role.error-assignment') });
      }
    ).finally(() => {
      this.loader.closeLoader('change-data-protection');
    });
  }

  getTextDefault(type:string){
    switch(type){
      case 'additional':
        return ''
      case 'protection':
        return this.translate.instant('protection-data-modal.text-default')
      default:
        return this.translate.instant('protection-data-modal.text-default-legal')
    }
  }

  getUpdateUrl(type:string){
    switch(type){
      case 'additional':
        return 'extra-info';
      case 'legal':
        return 'legal-info';
      default:
        return 'data-treatments'
    }
  }

  changeSelectedLanguage(type:string, selectedLanguage:any){
    this.referenceDocument[type + 'LanguageSelected'] = selectedLanguage;
    this.referenceDocument.additionalLanguageSelected.text = selectedLanguage.text;
  }

  editDataProtectionLanguages(type:string){
    let selectedLanguages = this.referenceDocument[type+'SelectedLanguages'];
    for(let i = 0; i < selectedLanguages.length; i++){
        let language = selectedLanguages[i];
        language.active = language.listValueId === this.referenceDocument[type+'LanguageSelected'].listValueId?true:false;
    }
    this.configureData(type);
  }

  deleteDataProtectionLanguages(type:string){
    this.modalService.infoModal({ text: this.translate.instant('reference-document-modal.question-delete-data-protection'), typeButtons: 'cancelRemove' })
      .subscribe((result: any) => {
        if(result.result !== 'cancel'){
            this.changeData(type, [], 'delete');
        }
      });
  }

  configureAdditionalGroup(){
    if(this.referenceDocument.fieldContainerConfigurated){
      this.openConfigurationGroupConcept();
    }else{
      this.createGroupContainer();
    }
  }

  createGroupContainer(){
    let newGroup:any = {
        name: this.translate.instant('form-configuration-modal.previous-data'),
        propertyVisibleValues: [],
        listVisibleValues: [],
        fieldVisibleValues: [],
        references: []
    };
    this.apiService.add('fieldContainers/'+ this.configuration.concept.conceptId, newGroup)
      .then((data:any)=>{
          this.referenceDocument.fieldContainerConfigurated = data;
          this.referenceDocument.fieldContainerConfiguratedPreview = cloneDeep(data);
          this.openConfigurationGroupConcept();
      }, ()=>{})
  }

  openConfigurationGroupConcept(){
    let configuration: any = {
      concept: this.configuration.concept,
      conceptInfo: this.referenceDocument.conceptInfo,
      fieldContainer: this.referenceDocument.fieldContainerConfiguratedPreview
    }
    this.modalService.formConfigurationModal(configuration)
      .subscribe((result: any) => {
          if(!result.fieldContainer.fields?.length){
            this.deleteAdditionalGroup(this.referenceDocument.fieldContainerConfigurated);
          }else if(result.result === 'ok' && result.changesInConfiguration){
            this.referenceDocument.fieldContainerConfigurated = cloneDeep(this.referenceDocument.fieldContainerConfiguratedPreview);
            this.referenceDocument.fieldContainerConfiguratedTranslation = cloneDeep(this.referenceDocument.fieldContainerConfiguratedPreview);
            this.hasTranslations = false;
            this.getFieldContainerTranslations();
            this.modalService.successModal({ text: this.translate.instant('reference-document-modal.configurated-form-configuration-ok') });
          }
      });
  }

  deleteAditionalGroupManually(container: any){
    this.modalService.infoModal({ text: this.translate.instant('reference-document-modal.question-delete-form-configuration'), typeButtons: 'cancelRemove' })
      .subscribe((result: any) => {
        if(result.result !== 'cancel'){
          this.loader.openLoader('reference-document-delete-group-manual');
          this.deleteAdditionalGroup(container, true);
        }
      });
  }

  deleteAdditionalGroup(container: any, manualProcess: boolean = false){
    this.apiService.delete('fieldContainers', container.fieldContainerId)
      .then(()=>{
        delete this.referenceDocument.fieldContainerConfigurated;
        delete this.referenceDocument.fieldContainerConfiguratedPreview;
        if(manualProcess){
          this.loader.closeLoader('reference-document-delete-group-manual');
          this.modalService.successModal({ text: this.translate.instant('reference-document-modal.confirmation-delete-form-configuration') });
        }
      }, (errorData:any)=>{
        this.loader.closeError();
      })
  }
  selectOption(type:string, action:string){
    if(type ==='configuration'){
      if(action === 'edit'){
        this.configureAdditionalGroup();
      } else if (action === 'translate'){
        if (this.hasTranslations) {
          this.openFormTranslationsModal();
        } else {
          this.loader.openLoader('open-form-translations-modal');
          this.hasTranslationsSubject.subscribe((data) => {
            this.loader.closeLoader('open-form-translations-modal');
            this.openFormTranslationsModal();
          });
        }
      } else {
        this.deleteAditionalGroupManually(this.referenceDocument.fieldContainerConfigurated)
      }
    }else{
      if(action === 'edit'){
        this.editDataProtectionLanguages(type)
      }else{
        this.deleteDataProtectionLanguages(type)
      }
    }
  }

  openFormTranslationsModal() {
    let configuration = {
      fieldContainer: this.referenceDocument.fieldContainerConfiguratedPreview,
      fieldTranslations: cloneDeep(this.referenceDocument.fieldContainerTranslations),
      conceptInfo: this.referenceDocument.conceptInfo,
    }
    this.modalService.formTranslations(configuration)
      .subscribe((result: any) => {
        if (result.result === 'ok') {
          this.modalService.adviceModal({
            typeModal: 'simple',
            type: 'success',
            typeButtons: 'accept',
            text: this.translate.instant('form-translations-modal.save-success')
          });
          this.referenceDocument.fieldContainerTranslations = cloneDeep(result.translations);
          this.checkTranslatedLanguages();
          this.addTranslationsToSelect();
        }
      })
  }

  openActionMenu() {
    this.referenceDocument.actionMenuForm.expandedMenu = cloneDeep(this.expandedMenu['menuConfigurationEditableFields'].menu);
    if(!this.referenceDocument.fieldContainerConfigurated || this.referenceDocument.conceptInfo?.disabled || this.referenceDocument.conceptInfo?.hasInstances){
      this.referenceDocument.actionMenuForm.expandedMenu.splice(2, 1);
    }
  }

  handleAccept() {
    this.dialogRef.close({
      result: 'ok',
      referenceDocuments: this.referenceDocument.documentationList,
      protectionData: this.referenceDocument.protectionData,
      additionalData: this.referenceDocument.additionalData,
      legalData: this.referenceDocument.legalData,
      additionalCfgOk: (this.referenceDocument.documentationList?.length || this.referenceDocument.protectionData?.length || this.referenceDocument.additionalData?.length || this.referenceDocument.legalData?.length || this.referenceDocument.fieldContainerConfigurated?.fields?.length)?true:false
    });
  }

  ngOnDestroy(): void {
    this.hasTranslationsSubject.unsubscribe();
  }
}
